import * as React from 'react';
import { memo, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { Form, Input, InputLabel, Button, InputPhone } from 'atoms';
import {
  IToggleCheckBoxButtons,
  ToggleCheckBoxList,
  PopupErrors,
  PopupContainer,
} from 'molecules';

import { IFieldError, ErrorsType } from 'types';
import { getFontFamily } from 'services/FontService';

export interface IUserPopup {
  editing: boolean;
  onDelete: () => void;
  onClose: () => void;
  onInputChange: (value: string | IToggleCheckBoxButtons, name: string) => void;
  handleSubmit: (e?: React.FormEvent<HTMLFormElement> | undefined) => void;
  values: IUserPopupValues;
  disabledButton: boolean;
  errors?: ErrorsType<IUserPopupValues> | null;
  errorMessage?: string | null;
  loading: boolean;
  /**
   * ТЗ 0083: поле «Рабочая почта Google Workspace» — только Admin (сервер
   * отказывает остальным). Нет — поле не показывается.
   */
  workspace?: IUserPopupWorkspace | null;
}

export interface IUserPopupWorkspace {
  hint: string;
  /** Под полем: откуда взялся адрес («подставлен логин»). */
  note?: string | null;
  /** Ошибка привязки — под полем, а не только внизу окна. */
  error?: string | null;
  /**
   * Окно правки: почта сохраняется своей кнопкой, независимо от остальной
   * формы (у сотрудника без телефона форма не сохраняется вовсе).
   */
  onSave?: () => void;
  saveLabel?: string;
  saving?: boolean;
  /** Адрес в поле отличается от привязанного — есть что сохранять. */
  dirty?: boolean;
  /** Строка результата после сохранения («Почта сохранена»). */
  savedNote?: string | null;
}

export interface IUserPopupValues {
  lastname: string;
  firstname: string;
  middlename: string;
  email: string;
  phone: string;
  roles: IToggleCheckBoxButtons;
  workspaceEmail: string;
}

export type IUserPopupErrors = {
  [key in keyof IUserPopupValues]?: IFieldError;
};

export const UserPopupComponent = memo(
  ({
    values,
    editing,
    onDelete,
    onClose,
    onInputChange,
    handleSubmit,
    errors,
    errorMessage,
    disabledButton,
    loading,
    workspace,
  }: IUserPopup) => {
    const { t } = useTranslation();
    const handleToggleCheckBoxListChange = useCallback(
      (activeList: IToggleCheckBoxButtons) => {
        onInputChange(activeList, 'roles');
      },
      [onInputChange],
    );

    return (
      <PopupContainer
        title={
          editing ? `${t('userPopup.editUser')}` : `${t('userPopup.addUser')}`
        }
        onClose={onClose}
      >
        <Form onSubmit={handleSubmit}>
          <FormInner>
            <InputWrapper>
              <InputLabel>{t('userPopup.fields.lastName.label')}</InputLabel>
              <Input
                placeholder={t('userPopup.fields.lastName.placeholder')}
                max={20}
                type="text"
                value={values.lastname}
                name="lastname"
                onChange={({ target: { value } }) =>
                  onInputChange(value, 'lastname')
                }
                hasError={Boolean(errors?.lastname)}
              />
            </InputWrapper>
            <InputWrapper>
              <InputLabel>{t('userPopup.fields.firstName.label')}</InputLabel>
              <Input
                placeholder={t('userPopup.fields.firstName.placeholder')}
                max={20}
                type="text"
                value={values.firstname}
                name="firstname"
                onChange={({ target: { value } }) =>
                  onInputChange(value, 'firstname')
                }
                hasError={Boolean(errors?.firstname)}
              />
            </InputWrapper>
            <InputWrapper>
              <InputLabel isOptional>
                {t('userPopup.fields.middleName.label')}
              </InputLabel>
              <Input
                placeholder={t('userPopup.fields.middleName.placeholder')}
                max={20}
                type="text"
                value={values.middlename}
                name="middlename"
                onChange={({ target: { value } }) =>
                  onInputChange(value, 'middlename')
                }
                hasError={Boolean(errors?.middlename)}
              />
            </InputWrapper>
            <InputWrapper>
              <InputLabel>{t('userPopup.fields.email.label')}</InputLabel>
              <Input
                placeholder={t('userPopup.fields.email.placeholder')}
                type="text"
                value={values.email}
                name="email"
                onChange={({ target: { value } }) =>
                  onInputChange(value, 'email')
                }
                hasError={Boolean(errors?.email)}
              />
            </InputWrapper>
            <InputWrapper>
              <InputLabel>{t('userPopup.fields.phone.label')}</InputLabel>
              <InputPhone
                placeholder={t('userPopup.fields.phone.placeholder')}
                value={values.phone}
                name="phone"
                onChange={({ target: { value } }) =>
                  onInputChange(value, 'phone')
                }
                hasError={Boolean(errors?.phone)}
              />
            </InputWrapper>
          </FormInner>
          {workspace && (
            <WorkspaceWrapper>
              <InputLabel isOptional>{t('staffMailbox.label')}</InputLabel>
              <WorkspaceRow>
                <WorkspaceInput>
                  {/* type="text": адрес проверяет сервер и отвечает на языке
                      интерфейса — всплывашка браузера была бы на своём. */}
                  <Input
                    placeholder={t('staffMailbox.placeholder')}
                    type="text"
                    value={values.workspaceEmail}
                    name="workspaceEmail"
                    onChange={({ target: { value } }) =>
                      onInputChange(value, 'workspaceEmail')
                    }
                    hasError={Boolean(workspace.error)}
                  />
                </WorkspaceInput>
                {workspace.onSave && (
                  <WorkspaceSave>
                    <Button
                      type="button"
                      variant="outline"
                      loading={workspace.saving}
                      disabled={!workspace.dirty || workspace.saving}
                      onClick={workspace.onSave}
                      colors={{
                        backgroundColor: 'var(--k-bg-ffffff)',
                        color: 'var(--k-fg-0d53fc)',
                      }}
                    >
                      {workspace.saveLabel}
                    </Button>
                  </WorkspaceSave>
                )}
              </WorkspaceRow>
              {workspace.error && (
                <WorkspaceError role="alert">{workspace.error}</WorkspaceError>
              )}
              {!workspace.error && workspace.savedNote && (
                <WorkspaceNote role="status">
                  {workspace.savedNote}
                </WorkspaceNote>
              )}
              {workspace.note && (
                <WorkspaceNote>{workspace.note}</WorkspaceNote>
              )}
              <WorkspaceHint>{workspace.hint}</WorkspaceHint>
            </WorkspaceWrapper>
          )}
          <ToggleCheckboxWrapper>
            <InputWrapper>
              <InputLabel>{t('userPopup.fields.roles.label')}</InputLabel>
              <ToggleCheckBoxList
                values={values.roles}
                onChange={handleToggleCheckBoxListChange}
                hasError={Boolean(errors?.roles)}
              />
            </InputWrapper>
          </ToggleCheckboxWrapper>
          <BottomWrapper>
            <ErrorWrapper>
              <PopupErrors>{errorMessage}</PopupErrors>
            </ErrorWrapper>
            {editing && (
              <DeleteButtonWrapper>
                <Button
                  onClick={onDelete}
                  type="button"
                  variant="outline"
                  colors={{
                    backgroundColor: 'var(--k-bg-ffffff)',
                    color: 'var(--k-fg-0d53fc)',
                  }}
                >
                  {t('userPopup.button.delete')}
                </Button>
              </DeleteButtonWrapper>
            )}
            <ButtonWrapper>
              <Button
                loading={loading}
                type="submit"
                colors={{
                  backgroundColor: 'var(--k-bg-0d53fc)',
                  color: 'var(--k-fg-ffffff)',
                  disabledColor: 'var(--k-fg-ffffff)',
                  bgDisabledColor: 'var(--k-bg-616e78)',
                }}
                disabled={disabledButton}
              >
                {editing
                  ? `${t('userPopup.button.save')}`
                  : `${t('userPopup.button.addUser')}`}
              </Button>
            </ButtonWrapper>
          </BottomWrapper>
        </Form>
      </PopupContainer>
    );
  },
);

const FormInner = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  width: 100%;
  margin-top: 40px;
`;

const InputWrapper = styled.div`
  width: calc(50% - 32px);

  &:nth-child(2n - 1) {
    margin-right: 32px;
  }

  &:nth-last-child(n - 2) {
    margin-bottom: 24px;
  }
`;

const WorkspaceWrapper = styled.div`
  width: 100%;
  margin-bottom: 24px;
`;

const WorkspaceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const WorkspaceInput = styled.div`
  flex: 1;
  min-width: 0;
`;

/**
 * `colors` кнопки перебивает её собственный вид «неактивна» — без
 * приглушения нетронутое поле выглядело так, будто почту можно сохранить.
 */
const WorkspaceSave = styled.div`
  flex-shrink: 0;
  width: 180px;

  button:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const WorkspaceError = styled.div`
  margin-top: 6px;
  font-family: ${getFontFamily()};
  font-size: 12px;
  line-height: 16px;
  color: var(--k-fg-f44336);
`;

const WorkspaceNote = styled.div`
  margin-top: 6px;
  font-family: ${getFontFamily()};
  font-size: 12px;
  line-height: 16px;
  color: var(--k-fg-0d53fc);
`;

const WorkspaceHint = styled.div`
  margin-top: 6px;
  font-family: ${getFontFamily()};
  font-size: 12px;
  line-height: 16px;
  color: var(--k-fg-616e78);
`;

const ToggleCheckboxWrapper = styled.div`
  margin -top: 24px;

  ${InputWrapper} {
    width: 100%;
  }
`;

const BottomWrapper = styled.div`
  display: flex;
  width: 100%;
  margin-top: 16px;
`;

const ButtonWrapper = styled.div`
  flex-shrink: 0;
  width: 224px;
  margin-left: 16px;
`;

const ErrorWrapper = styled.div`
  flex-grow: 1;
  margin-right: 52px;
`;

const DeleteButtonWrapper = styled.div`
  flex-shrink: 0;
  width: 127px;
  margin-left: 16px;
`;
